import React, { useRef, useEffect, useState, useCallback } from 'react';

const RESIZE_HANDLE_SIZE = 12;

const SimpleCanvas = React.forwardRef(({
  components,
  onUpdateComponentPosition,
  onUpdateComponentSize,
  onComponentDrop,
  onDeleteComponent,
  width = 1280,
  height = 720
}, ref) => {
  const localRef = useRef(null);
  const canvasRef = ref || localRef;
  const canvasContainerRef = useRef(null);
  
  // Canvas state
  const [context, setContext] = useState(null);
  const [imageCache, setImageCache] = useState({});
  
  // Component interaction state
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeIndex, setResizeIndex] = useState(null);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });

  // Copy/cut/paste state
  const [clipboardComponent, setClipboardComponent] = useState(null);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = canvasContainerRef.current;
    if (canvas && container) {
      const ctx = canvas.getContext('2d');
      setContext(ctx);
      
      const updateCanvasSize = () => {
        const rect = container.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        canvas.style.width = rect.width + 'px';
        canvas.style.height = rect.height + 'px';
        
        ctx.scale(dpr, dpr);
      };
      
      updateCanvasSize();
      
      const resizeObserver = new ResizeObserver(updateCanvasSize);
      resizeObserver.observe(container);
      
      return () => resizeObserver.disconnect();
    }
  }, [canvasRef]);

  // Load images into cache
  useEffect(() => {
    const loadImages = async () => {
      const newCache = { ...imageCache };
      let hasChanges = false;
      
      for (const component of components) {
        if (component.component && component.component.image && !newCache[component.component.image]) {
          try {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
              img.src = component.component.image;
            });
            newCache[component.component.image] = img;
            hasChanges = true;
          } catch (error) {
            console.error('Failed to load image:', component.component.image);
          }
        }
      }
      
      if (hasChanges) {
        setImageCache(newCache);
      }
    };
    
    loadImages();
  }, [components, imageCache]);

  // Draw function
  const draw = useCallback(() => {
    if (!context || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Clear canvas
    context.clearRect(0, 0, rect.width, rect.height);

    // Fill with white background
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, rect.width, rect.height);
    
    // Draw components
    components.forEach((component, index) => {
      if (!component.component || !component.component.image) return;
      
      const img = imageCache[component.component.image];
      if (!img) return;
      
      context.save();
      
      // Apply component transformation
      const centerX = component.x;
      const centerY = component.y;
      
      context.translate(centerX, centerY);
      
      if (component.rotation) {
        context.rotate((component.rotation * Math.PI) / 180);
      }
      
      // Draw image
      const drawX = -component.width / 2;
      const drawY = -component.height / 2;
      
      try {
        context.drawImage(img, drawX, drawY, component.width, component.height);
      } catch (error) {
        console.error('Error drawing image:', error);
      }
      
      // Draw selection outline if selected
      if (index === selectedIndex) {
        context.strokeStyle = '#c4a778';
        context.lineWidth = 2;
        context.strokeRect(drawX - 2, drawY - 2, component.width + 4, component.height + 4);

        // Draw resize handles
        const handleSize = RESIZE_HANDLE_SIZE;
        context.fillStyle = '#c4a778';
        context.fillRect(drawX + component.width - handleSize/2, drawY + component.height - handleSize/2, handleSize, handleSize);
      }
      
      context.restore();
    });
  }, [context, canvasRef, components, imageCache, selectedIndex]);

  // Animation loop
  useEffect(() => {
    let animationId;
    
    const animate = () => {
      draw();
      animationId = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [draw]);

  // Mouse event handlers
  const getMousePos = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  }, [canvasRef]);

  const findComponentAt = useCallback((x, y) => {
    for (let i = components.length - 1; i >= 0; i--) {
      const component = components[i];
      const left = component.x - component.width / 2;
      const right = component.x + component.width / 2;
      const top = component.y - component.height / 2;
      const bottom = component.y + component.height / 2;
      
      if (x >= left && x <= right && y >= top && y <= bottom) {
        return i;
      }
    }
    return null;
  }, [components]);

  const handleMouseDown = useCallback((e) => {
    const pos = getMousePos(e);
    const componentIndex = findComponentAt(pos.x, pos.y);

    if (componentIndex !== null) {
      const component = components[componentIndex];

      // Select the component
      setSelectedIndex(componentIndex);

      // Check if clicking on resize handle (only if this component is already selected)
      const handleX = component.x + component.width / 2;
      const handleY = component.y + component.height / 2;
      const handleSize = RESIZE_HANDLE_SIZE;

      if (componentIndex === selectedIndex && Math.abs(pos.x - handleX) < handleSize && Math.abs(pos.y - handleY) < handleSize) {
        setIsResizing(true);
        setResizeIndex(componentIndex);
        setResizeStart({
          x: pos.x,
          y: pos.y,
          width: component.width,
          height: component.height
        });
        e.preventDefault();
      } else {
        setIsDragging(true);
        setDraggedIndex(componentIndex);
        setDragOffset({
          x: pos.x - component.x,
          y: pos.y - component.y
        });
        e.preventDefault();
      }
    } else {
      // Clicked outside any component - deselect
      setSelectedIndex(null);
      e.preventDefault();
    }
  }, [getMousePos, findComponentAt, components, selectedIndex]);

  const handleMouseMove = useCallback((e) => {
    const pos = getMousePos(e);
    
    if (isDragging && draggedIndex !== null && onUpdateComponentPosition) {
      const newX = pos.x - dragOffset.x;
      const newY = pos.y - dragOffset.y;
      onUpdateComponentPosition(draggedIndex, newX, newY);
    } else if (isResizing && resizeIndex !== null && onUpdateComponentSize) {
      const deltaX = pos.x - resizeStart.x;
      const deltaY = pos.y - resizeStart.y;
      const newWidth = Math.max(20, resizeStart.width + deltaX);
      const newHeight = Math.max(20, resizeStart.height + deltaY);
      onUpdateComponentSize(resizeIndex, newWidth, newHeight);
    }
  }, [getMousePos, isDragging, draggedIndex, dragOffset, isResizing, resizeIndex, resizeStart, onUpdateComponentPosition, onUpdateComponentSize]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDraggedIndex(null);
    setDragOffset({ x: 0, y: 0 });
    setIsResizing(false);
    setResizeIndex(null);
    setResizeStart({ x: 0, y: 0, width: 0, height: 0 });
  }, []);

  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    
    if (onComponentDrop) {
      const pos = getMousePos(e);
      const dragData = e.dataTransfer.getData('text/plain');
      
      try {
        const component = JSON.parse(dragData);
        onComponentDrop(component, pos.x, pos.y);
      } catch (error) {
        console.error('Failed to parse drop data:', error);
      }
    }
  }, [getMousePos, onComponentDrop]);

  // Keyboard event handlers for delete, copy, cut, paste
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Skip if focused on input/textarea
      const target = e.target;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      const isCmdOrCtrl = e.metaKey || e.ctrlKey;

      // Delete or Backspace
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIndex !== null && onDeleteComponent) {
        e.preventDefault();
        onDeleteComponent(selectedIndex);
        setSelectedIndex(null);
      }

      // Command/Ctrl + C (Copy)
      if (isCmdOrCtrl && e.key === 'c' && selectedIndex !== null) {
        e.preventDefault();
        const componentToCopy = components[selectedIndex];
        if (componentToCopy) {
          setClipboardComponent({ ...componentToCopy });
          console.log('Component copied to clipboard');
        }
      }

      // Command/Ctrl + X (Cut)
      if (isCmdOrCtrl && e.key === 'x' && selectedIndex !== null && onDeleteComponent) {
        e.preventDefault();
        const componentToCut = components[selectedIndex];
        if (componentToCut) {
          setClipboardComponent({ ...componentToCut });
          onDeleteComponent(selectedIndex);
          setSelectedIndex(null);
          console.log('Component cut to clipboard');
        }
      }

      // Command/Ctrl + V (Paste)
      if (isCmdOrCtrl && e.key === 'v' && clipboardComponent && onComponentDrop) {
        e.preventDefault();
        // Paste at a slightly offset position
        const newX = clipboardComponent.x + 20;
        const newY = clipboardComponent.y + 20;
        const pastedComponent = {
          ...clipboardComponent,
          id: `component_${Date.now()}`,
          x: newX,
          y: newY,
          component: {
            ...clipboardComponent.component,
            id: `comp_${Date.now()}`
          }
        };
        // Use onComponentDrop to add the component
        onComponentDrop(pastedComponent.component, newX, newY);
        console.log('Component pasted from clipboard');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, clipboardComponent, components, onDeleteComponent, onComponentDrop]);

  // Export functions
  useEffect(() => {
    if (canvasRef.current) {
      canvasRef.current.getCleanDataURL = () => {
        return canvasRef.current.toDataURL('image/png');
      };
    }
  }, [canvasRef]);

  return (
    <div 
      ref={canvasContainerRef} 
      className="w-full h-full relative overflow-hidden"
      style={{ cursor: isDragging ? 'grabbing' : 'default' }}
    >
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className="w-full h-full"
      />
    </div>
  );
});

SimpleCanvas.displayName = 'SimpleCanvas';

export default SimpleCanvas;
